import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { isSafeDashboardUrl, reconcilePush } from '@/lib/push';
import { stateForDashboardLink } from '@/lib/deepLinks';

/**
 * Background glue for Web Push. It renders nothing and NEVER asks for a permission.
 *  - When a signed-in person opens the app, a device that already opted in is re-checked quietly
 *    (renewed subscription, withdrawn permission, last-seen).
 *  - When a notification is clicked while Ridalo is open, the service worker asks the page to go
 *    to the booking / conversation: the app navigates in place (history and session kept).
 */
export const PushRuntime = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en' : 'fr';
  const userId = user?.user_id;

  useEffect(() => {
    if (!userId) return;
    reconcilePush({ language, userId }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event) => {
      const data = event.data || {};
      if (data.type === 'ridalo:navigate' && isSafeDashboardUrl(data.url)) {
        navigate(data.url, { state: stateForDashboardLink(data.url, window.location.pathname) });
      } else if (data.type === 'ridalo:push-resubscribe' && userId) {
        reconcilePush({ language, userId }).catch(() => {});
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate, userId, language]);

  return null;
};
